/**
 * HierarchyView adapter wrapping gantt's task tree.
 *
 * Implements `05-` §C.3 with D-35 eager materialization.
 *
 * Per D-35 (3), `ancestorsOf`, `descendantsOf`, `leavesOf` return cached
 * arrays — eagerly computed at construction so iteration over the snapshot
 * is stable even if gantt's underlying tree mutates between adapter calls.
 * The cached arrays are shared per HierarchyView instance; they're
 * discarded when the snapshot is rebuilt.
 */

import type { HierarchyView, NormalizedTaskID, TaskID } from "../core/types";
import { normalizeTaskId } from "../core/ids";

/**
 * Minimal subset of gantt we touch. Accepts a structural interface for
 * testability.
 */
export interface GanttHierarchyBackend {
  /** Iterate tasks in DFS order; callback receives task object with at least .id, .parent. */
  eachTask(callback: (task: { id: TaskID; parent?: TaskID; [k: string]: unknown }) => void, parent?: TaskID): void;
  /** Returns parent ID. Root tasks return gantt's root_id (often 0 or "0"). */
  getParent(idOrTask: TaskID): TaskID;
  /** Returns array of immediate child IDs (empty if none). */
  getChildren(parentId: TaskID): TaskID[];
  /** True if the task is a summary/project. */
  isSummaryTask(taskOrId: { id: TaskID } | TaskID): boolean;
  /** Returns task object (we may consult `type` to detect summary). */
  getTask(id: TaskID): { id: TaskID; parent?: TaskID; type?: string; [k: string]: unknown };
  /** Returns true if the ID corresponds to a known task. */
  isTaskExists(id: TaskID): boolean;
  /** Root id value (gantt's config.root_id; typically 0). */
  config: { root_id?: TaskID };
}

/**
 * Eager-materialized HierarchyView. The constructor walks the entire tree
 * once; subsequent lookups are O(1) array reads.
 */
export class GanttHierarchyView implements HierarchyView {
  private readonly parentMap = new Map<NormalizedTaskID, NormalizedTaskID | null>();
  private readonly childrenMap = new Map<NormalizedTaskID, NormalizedTaskID[]>();
  private readonly summarySet = new Set<NormalizedTaskID>();

  // Materialized caches per D-35.
  private readonly ancestorsCache = new Map<NormalizedTaskID, NormalizedTaskID[]>();
  private readonly descendantsCache = new Map<NormalizedTaskID, NormalizedTaskID[]>();
  private readonly leavesCache = new Map<NormalizedTaskID, NormalizedTaskID[]>();

  constructor(gantt: GanttHierarchyBackend, includedTaskIds: ReadonlySet<NormalizedTaskID>) {
    const rootId = normalizeTaskId(gantt.config.root_id ?? 0);

    // Walk gantt's tasks via eachTask and record parent/children.
    gantt.eachTask((task) => {
      const id = normalizeTaskId(task.id);
      if (!includedTaskIds.has(id)) return;
      const rawParent = task.parent;
      // Gantt root tasks have parent === root_id; map that to null.
      const parent = (rawParent === undefined || rawParent === null || normalizeTaskId(rawParent) === rootId)
        ? null
        : (includedTaskIds.has(normalizeTaskId(rawParent)) ? normalizeTaskId(rawParent) : null);
      this.parentMap.set(id, parent);
      if (gantt.isSummaryTask(task)) this.summarySet.add(id);
      if (parent !== null) {
        let kids = this.childrenMap.get(parent);
        if (!kids) { kids = []; this.childrenMap.set(parent, kids); }
        kids.push(id);
      }
      // Ensure every visited task has an entry in childrenMap (even if []).
      if (!this.childrenMap.has(id)) this.childrenMap.set(id, []);
    });

    // Pre-materialize caches per D-35 (3).
    for (const id of this.parentMap.keys()) {
      this.materializeAncestors(id);
      this.materializeDescendantsAndLeaves(id);
    }
  }

  parentOf(taskId: NormalizedTaskID): NormalizedTaskID | null {
    return this.parentMap.get(taskId) ?? null;
  }

  childrenOf(taskId: NormalizedTaskID): readonly NormalizedTaskID[] {
    return this.childrenMap.get(taskId) ?? [];
  }

  isSummary(taskId: NormalizedTaskID): boolean {
    return this.summarySet.has(taskId);
  }

  ancestorsOf(taskId: NormalizedTaskID): readonly NormalizedTaskID[] {
    return this.ancestorsCache.get(taskId) ?? [];
  }

  descendantsOf(taskId: NormalizedTaskID): readonly NormalizedTaskID[] {
    return this.descendantsCache.get(taskId) ?? [];
  }

  leavesOf(taskId: NormalizedTaskID): readonly NormalizedTaskID[] {
    return this.leavesCache.get(taskId) ?? [];
  }

  isDescendantOf(descendantId: NormalizedTaskID, ancestorId: NormalizedTaskID): boolean {
    const anc = this.ancestorsCache.get(descendantId);
    if (!anc) return false;
    return anc.indexOf(ancestorId) !== -1;
  }

  private materializeAncestors(taskId: NormalizedTaskID): NormalizedTaskID[] {
    const cached = this.ancestorsCache.get(taskId);
    if (cached) return cached;
    const acc: NormalizedTaskID[] = [];
    let cur = this.parentMap.get(taskId) ?? null;
    while (cur !== null) {
      acc.push(cur);
      cur = this.parentMap.get(cur) ?? null;
    }
    this.ancestorsCache.set(taskId, acc);
    return acc;
  }

  private materializeDescendantsAndLeaves(taskId: NormalizedTaskID): void {
    if (this.descendantsCache.has(taskId)) return;
    const descendants: NormalizedTaskID[] = [];
    const leaves: NormalizedTaskID[] = [];
    const stack: NormalizedTaskID[] = [...(this.childrenMap.get(taskId) ?? [])];
    while (stack.length > 0) {
      const id = stack.pop() as NormalizedTaskID;
      descendants.push(id);
      const kids = this.childrenMap.get(id) ?? [];
      if (kids.length === 0) {
        leaves.push(id);
      } else {
        for (const k of kids) stack.push(k);
      }
    }
    this.descendantsCache.set(taskId, descendants);
    this.leavesCache.set(taskId, leaves);
  }
}
